/**
 * The PLAYER stream's own state for one player (spec 02 §3): the C64's per-player registers from the
 * rebb64 disassembly, one field each, so the port reads line by line against the 6502 code. Plain
 * numbers and booleans only, so the world stays serialisable (06 R5).
 */

import type { Command } from '../types';

export interface PlayerMotion {
  /**
   * D_8520, the sprite frame: 0-3 facing right (bit 1 set: the jump frames), 4-7 facing left, 8/9 blowing
   * right, $A/$B blowing left, $C-$E (and 0) spinning while dying, $F-$12 dead. Bit 0 is the animation step.
   */
  frame: number;
  /** D_8610: animation counter (idle, walk, the every-2nd-tick air checks) and the death animation's timer. */
  anim: number;
  /** D_87A0: jump rise steps left (15..1); 0 at the apex and while descending; -1 not jumping. */
  jump: number;
  /** D_87C8: descent step (0..16); -1 before the apex. */
  descent: number;
  /** D_87F0 >= 0: falling at 2 px per tick (walked off a ledge, or the jump ended over nothing). */
  falling: boolean;
  /** D_8840 / D_8868: takeoff drift, 1 px per tick to the left / right for the whole jump. */
  driftLeft: boolean;
  driftRight: boolean;
  /** D_8818: blow animation ticks left (6..0); -1 not blowing. */
  blow: number;
  /** D_ACCB: 0 when the blow began facing right, 2 facing left. */
  blowSide: number;
  /** Main-loop passes completed when blowing is allowed again (the C64's D_A824 back at 0). */
  blowReadyAt: number;
  /** This I-tick's controls as C64 joystick bits, set when pressed: 1 up (jump), 4 left, 8 right, 16 fire. */
  joy: number;
  /** Moved by walking this I-tick (the renderer's walk pose; the C64 has no separate walk frames). */
  walked: boolean;
  /** D_86D8 = $12: the death animation has begun (its timer is set on the first tick). */
  deathStarted: boolean;
  /** State $0F: the death animation is over, waiting for the main loop's `check_player_state`. */
  dead: boolean;
  /** Main-loop passes completed when `dead` was set; a later pass handles the respawn. */
  deadAt: number;
  /** D_8548 toggled every tick of respawn invulnerability: true = drawn in the flash colour. */
  flash: boolean;
  /** Power-ups (spec 02 §7.5, M2): candies, rings and shoe last until death (D_7F53 clears them). */
  shoe: boolean;
  yellowCandy: boolean;
  purpleCandy: boolean;
  blueCandy: boolean;
  redRing: boolean;
  purpleRing: boolean;
  blueRing: boolean;
}

/** The joystick bits the C64 reads (active low there, set-when-pressed here): D_85E8 for P1, D_85E9 for P2. */
export const JOY_UP = 0x01;
export const JOY_LEFT = 0x04;
export const JOY_RIGHT = 0x08;
export const JOY_FIRE = 0x10;

/** A frame's controls as joystick bits. */
export function joyBits(c: Command): number {
  return (c.jump ? JOY_UP : 0) | (c.left ? JOY_LEFT : 0) | (c.right ? JOY_RIGHT : 0) | (c.fire ? JOY_FIRE : 0);
}

export function initialMotion(): PlayerMotion {
  return {
    frame: 0, anim: 0, jump: -1, descent: -1, falling: false, driftLeft: false, driftRight: false,
    blow: -1, blowSide: 0, blowReadyAt: 0, joy: 0, walked: false, deathStarted: false, dead: false, deadAt: 0,
    flash: false, shoe: false, yellowCandy: false, purpleCandy: false, blueCandy: false,
    redRing: false, purpleRing: false, blueRing: false,
  };
}

/** Everything D_7F53 resets when a life ends: candies, rings and shoe. */
export function losePowerups(m: PlayerMotion): void {
  m.shoe = false;
  m.yellowCandy = false;
  m.purpleCandy = false;
  m.blueCandy = false;
  m.redRing = false;
  m.purpleRing = false;
  m.blueRing = false;
}
